import React, { useState, useRef, useEffect } from "react";
import { Input, Label } from "reactstrap";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker as MuiTimePicker } from "@mui/x-date-pickers/TimePicker";

const InputReturn = ({
  type,
  name,
  fieldKey,
  handleInput,
  data,
  formData,
  placeholder = "",
  options = [],
  checkboxOptions = [],
  optionKey = "label",
  optionValue = "value",
  validation = {},
  min,
  max,
  error = {},
  onBlurValidate,
  required = false,
  // Pagination props (optional)
  onFetchPage = null, // function(nextPage:number) => void
  paginated = false,
  hasMore = false,
  loading = false,
  currentPage = 1,
  searchable = false,
  onSearch = null, // function(query:string) => void — server-side search callback (schema sources)
  numberRangeOptions = null,
}) => {
  const fieldId = `${fieldKey}-input`;
  const errMsg = error?.[fieldKey];
  const RequiredMark = required ? <span style={{ color: "red", marginLeft: 2 }}>*</span> : null;

  // Helper to safely derive a display label for an option object
  const getOptionLabel = (option) => {
    if (option === null || option === undefined) return "";
    if (typeof option === "string" || typeof option === "number") return String(option);
    // prefer explicit keys, then fall back to common aliases
    return (
      option[optionKey] ?? option.label ?? option.name ?? option.key ?? option.value ?? JSON.stringify(option)
    );
  };

  const onBlur = () => {
    if (typeof onBlurValidate === "function") onBlurValidate();
  };

  // Hooks required for the select dropdown behavior must be called unconditionally
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const searchDebounceRef = useRef(null);
  // Cache the label of the currently-selected option so the trigger button stays
  // readable even while options are being reloaded (e.g. after a search clears them).
  const [selectedOptionLabel, setSelectedOptionLabel] = useState(null);

  // Reset cached label when the value is cleared externally (form reset / cascade reset)
  useEffect(() => {
    if (data === null || data === undefined || data === "") {
      setSelectedOptionLabel(null);
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync cached label from options list whenever options or data changes.
  // This covers the initial-load / edit-mode case where data is pre-populated
  // but selectedOptionLabel hasn't been set yet.
  useEffect(() => {
    if (data === null || data === undefined || data === "") return;
    if (!Array.isArray(options) || options.length === 0) return;
    const found = options.find((o) => {
      const v = (o && typeof o === "object") ? (o[optionValue] ?? o.value ?? "") : o;
      return String(v) === String(data);
    });
    if (found) setSelectedOptionLabel(getOptionLabel(found));
  }, [options, data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear the search query (and restore full options for server-side sources) when the dropdown closes
  useEffect(() => {
    if (!open && searchText) {
      setSearchText("");
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      if (typeof onSearch === "function") {
        onSearch(""); // restore unfiltered options
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onDocClick = (ev) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  switch (type) {
    case "text":
      return (
        <div className="w-100">
          <Label htmlFor={fieldId}>{name}{RequiredMark}</Label>
          <Input
            id={fieldId}
            type="text"
            onChange={(e) => handleInput(e)}
            onBlur={onBlur}
            value={data ?? ""}
            placeholder={placeholder}
            invalid={!!errMsg}
          />
          {errMsg && <div className="invalid-feedback d-block">{errMsg}</div>}
        </div>
      );

    case "number":
      if (numberRangeOptions && numberRangeOptions.length > 0) {
        return (
          <div className="w-100">
            <Label htmlFor={fieldId}>{name}</Label>
            <Input
              id={fieldId}
              type="select"
              onChange={(e) => handleInput(e)}
              onBlur={onBlur}
              value={data ?? ""}
              invalid={!!errMsg}
            >
              <option value="">-- Select --</option>
              {numberRangeOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </Input>
            {errMsg && <div className="invalid-feedback d-block">{errMsg}</div>}
          </div>
        );
      }
      return (
        <div className="w-100">
          <Label htmlFor={fieldId}>{name}{RequiredMark}</Label>
          <Input
            id={fieldId}
            type="number"
            onChange={(e) => handleInput(e)}
            onBlur={onBlur}
            value={data ?? ""}
            min={min}
            max={max}
            placeholder={placeholder}
            invalid={!!errMsg}
          />
          {errMsg && <div className="invalid-feedback  d-block">{errMsg}</div>}
        </div>
      );

    case "textfield":
      return (
        <div className="w-100">
          <Label htmlFor={fieldId}>{name}{RequiredMark}</Label>
          <Input
            id={fieldId}
            type="textarea"
            onChange={(e) => handleInput(e)}
            onBlur={onBlur}
            value={data ?? ""}
            placeholder={placeholder}
            invalid={!!errMsg}
          />
          {errMsg && <div className="invalid-feedback d-block">{errMsg}</div>}
        </div>
      );

    case "select":
      // If a paginated source is provided, searchable, or options are extremely large,
      // render a select-like control with a dropdown that requests next pages.
      if (onFetchPage || paginated || searchable || (Array.isArray(options) && options.length > 2000)) {
        const handleOptionClick = (val, label) => {
          if (label !== undefined) setSelectedOptionLabel(label);
          handleInput({ target: { value: val } });
          setOpen(false);
        };

        const onScroll = (e) => {
          if (!onFetchPage) return;
          try {
            const el = e.target;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
              if (loading) return;
              if (!hasMore) return;
              const next = (Number(currentPage) || 1) + 1;
              onFetchPage(next);
            }
          } catch (err) {
            // ignore
          }
        };

        // Handle search input changes — debounce server-side calls, or filter client-side
        const handleSearchChange = (e) => {
          const val = e.target.value;
          setSearchText(val);
          if (typeof onSearch === "function") {
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = setTimeout(() => onSearch(val), 350);
          }
        };

        // For server-side search (onSearch provided) the API already returns filtered results.
        // For client-side (no onSearch), filter the loaded options locally.
        const displayOptions =
          typeof onSearch === "function" || !searchText
            ? options || []
            : (options || []).filter((o) =>
                getOptionLabel(o).toLowerCase().includes(searchText.toLowerCase())
              );

        const selectedLabel = (() => {
          if (data === null || data === undefined || data === "") return placeholder || "Please select";
          // Use the cached label (set on click or synced from options) so the trigger
          // keeps showing the readable label even while options are reloading.
          if (selectedOptionLabel !== null) return selectedOptionLabel;
          const found = (options || []).find((o) => {
            const val = (o && typeof o === 'object') ? (o[optionValue] ?? o.value ?? '') : o;
            return String(val) === String(data);
          });
          return found ? getOptionLabel(found) : String(data);
        })();

        return (
          <div className="w-100" ref={containerRef}>
            <Label htmlFor={fieldId}>{name}{RequiredMark}</Label>
            <div style={{ position: 'relative' }}>
              <Input type="hidden" name={fieldKey} value={data ?? ""} />

              <button
                type="button"
                id={fieldId}
                name={fieldKey}
                aria-haspopup="listbox"
                aria-expanded={open}
                className="form-control d-flex align-items-center justify-content-between"
                onClick={() => setOpen((s) => !s)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOpen(true);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedLabel}</span>
                <i className={`fa ${open ? 'fa-caret-up' : 'fa-caret-down'}`} />
              </button>

              {open && (
                <div
                  role="listbox"
                  tabIndex={-1}
                  className="shadow"
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    zIndex: 1060,
                    background: '#fff',
                    border: '1px solid #ced4da',
                    borderRadius: 4,
                    overflow: 'hidden',
                    marginTop: 4,
                  }}
                >
                  {/* Search bar — shown for paginated / schema sources */}
                  {searchable && (
                    <div
                      style={{
                        padding: '6px 8px',
                        borderBottom: '1px solid #e9ecef',
                        background: '#fff',
                      }}
                    >
                      <input
                        type="text"
                        value={searchText}
                        onChange={handleSearchChange}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Search..."
                        autoFocus
                        style={{
                          width: '100%',
                          border: '1px solid #ced4da',
                          borderRadius: 4,
                          padding: '4px 8px',
                          fontSize: 14,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  )}

                  {/* Scrollable options list */}
                  <div style={{ maxHeight: 175, overflow: 'auto' }} onScroll={onScroll}>
                    {Array.isArray(displayOptions) && displayOptions.length === 0 && !loading ? (
                      <div style={{ padding: 8, color: '#6c757d' }}>
                        {searchText ? 'No matching options' : placeholder || 'No options'}
                      </div>
                    ) : (
                      displayOptions.map((option, idx) => {
                        const val = (option && typeof option === 'object') ? (option[optionValue] ?? option.value ?? '') : option;
                        const label = getOptionLabel(option);
                        const isSelected = String(data) === String(val);
                        return (
                          <div
                            key={`${fieldKey}-opt-${idx}`}
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => handleOptionClick(val, label)}
                            onKeyDown={(e) => e.key === 'Enter' && handleOptionClick(val, label)}
                            tabIndex={0}
                            style={{
                              padding: '8px 10px',
                              borderBottom: '1px solid #f1f1f1',
                              cursor: 'pointer',
                              background: isSelected ? '#e9ecef' : '#fff',
                            }}
                          >
                            {label}
                          </div>
                        );
                      })
                    )}
                    {loading && <div style={{ padding: 8, color: '#6c757d' }}>Loading...</div>}
                    {!hasMore && Array.isArray(displayOptions) && displayOptions.length > 0 && (
                      <div style={{ padding: 8, textAlign: 'center', color: '#6c757d' }}>End of options</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {errMsg && <div className="invalid-feedback d-block">{errMsg}</div>}
          </div>
        );
      }

      return (
        <div className="w-100">
          <Label htmlFor={fieldId}>{name}{RequiredMark}</Label>
          <Input
            id={fieldId}
            type="select"
            name={fieldKey}
            value={data ?? ""}
            onChange={(e) => handleInput(e)}
            onBlur={onBlur}
            invalid={!!errMsg}
          >
            <option value="" disabled>
              {placeholder || "Please select"}
            </option>
            {options.map((option, index) => {
              const val = (option && typeof option === 'object') ? (option[optionValue] ?? option.value ?? '') : option;
              return (
                <option key={`${fieldKey}-opt-${index}`} value={val}>
                  {getOptionLabel(option)}
                </option>
              );
            })}
          </Input>
          {errMsg && <div className="invalid-feedback d-block">{errMsg}</div>}
        </div>
      );

    case "radio":
      return (
        <div className="m-t-15 w-100">
          <Label className="d-block">{name}{RequiredMark}</Label>
          <div className="ms-2 m-checkbox-inline">
            {options.map((radioValue, index) => {
              const val = (radioValue && typeof radioValue === 'object') ? (radioValue[optionValue] ?? radioValue.value ?? '') : radioValue;
              const id = `${fieldKey}-radio-${index}`;
              return (
                <div className="radio ms-2 radio-primary" key={id}>
                  <Input
                    type="radio"
                    id={id}
                    name={fieldKey}
                    value={val}
                    checked={data === val}
                    onChange={(e) => handleInput(e)}
                    onBlur={onBlur}
                    invalid={!!errMsg}
                  />
                  <Label className="mb-0" htmlFor={id}>
                    {getOptionLabel(radioValue)}
                  </Label>
                </div>
              );
            })}
          </div>
          {errMsg && <div className="invalid-feedback d-block">{errMsg}</div>}
        </div>
      );

    case "checkbox":
      return (
        <div className="m-t-15 w-100">
          <Label className="d-block">{name}{RequiredMark}</Label>
          <div className="ms-2 m-checkbox-inline custom-radio-ml d-flex align-middle flex-wrap">
            {checkboxOptions.map((checkbox, index) => {
              const id = `${fieldKey}-checkbox-${index}`;
              // const checked = !!formData?.[checkbox.key];              
              // data is the nested object for this checkbox group
              const groupObj = data || {};
              const checked = !!groupObj[checkbox.key];

              return (
                <div className="d-flex mt-2 me-3 align-middle" key={id}>
                  <Input
                    className="checkbox_animated relative"
                    type="checkbox"
                    id={id}
                    name={checkbox.key}
                    checked={checked}
                    onChange={(e) => handleInput(e, { optionKey: checkbox.key })}
                    onBlur={onBlur}
                  />
                  <Label className="mb-0 ms-1 fw-bold" htmlFor={id}>
                    {checkbox.key}
                  </Label>
                </div>
              );
            })}
          </div>
          {errMsg && <div className="invalid-feedback d-block">{errMsg}</div>}
        </div>
      );

    case "date":
      return (
        <div className="w-100">
          <Label className="d-block" htmlFor={fieldId}>
            {name}{RequiredMark}
          </Label>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MuiDatePicker
              value={data || null}
              minDate={min ? new Date(min) : undefined}
              maxDate={max ? new Date(max) : undefined}
              onChange={(d) => handleInput(d)}
              slotProps={{
                textField: {
                  id: fieldId,
                  size: "small",
                  fullWidth: true,
                  error: !!errMsg,
                  placeholder: placeholder || "Select date",
                  onBlur: onBlur,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                      fontSize: "14px",
                      background: "#fff",
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
          {errMsg && <div className="invalid-feedback d-block">{errMsg}</div>}
        </div>
      );

    case "time":
      return (
        <div className="w-100">
          <Label className="d-block" htmlFor={fieldId}>
            {name}{RequiredMark}
          </Label>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MuiTimePicker
              value={data || null}
              onChange={(d) => handleInput(d)}
              slotProps={{
                textField: {
                  id: fieldId,
                  size: "small",
                  fullWidth: true,
                  error: !!errMsg,
                  placeholder: placeholder || "Select time",
                  onBlur: onBlur,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                      fontSize: "14px",
                      background: "#fff",
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
          {errMsg && <div className="invalid-feedback d-block">{errMsg}</div>}
        </div>
      );

    default:
      return <p className="text-warning">Unknown input type: {String(type)}</p>;
  }
};

export default InputReturn